import React, { useEffect, useState } from "react";
import { Typography, TextField, Button, Box } from "@mui/material";
import { Icon } from "@iconify/react";
import { useOutletContext } from "react-router-dom";
import "../../../styles/Auth/report.css";
import { toast } from "react-toastify";
import { getData } from "../../../webService/webservice.js";
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import { useStore } from "../../../Context/context.jsx";

const ReportPage = ({ sessionToken }) => {
    const { startDate, endDate, dateRange } = useOutletContext();
    const [reportSummary, setReportSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [closingTotals, setClosingTotals] = useState({});

    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId = adminStoreIds[0] || "";
    const { selectedStoreId, setSelectedStoreId } = useStore();

    const [filterType, setFilterType] = useState("All");

    const fetchReportSummary = async () => {
        if (!startDate || !endDate) return;

        try {
            setLoading(true);
            const params = {
                storeId: selectedStoreId,
                startDate,
                endDate,
                filter: filterType
            };
            const response = await getData("reports/reports/summary", params, sessionToken);
            if (response?.status !== 200) {
                toast.error(response?.message || "Failed to fetch Report Summary");
                return;
            }
            const results = response?.data || {};
            setReportSummary(results);
            console.log("Report Summary", results);
            toast.success(response?.message || "Report Summary fetched successfully");
        } catch (error) {
            toast.error(error.message || "Failed to fetch Report Summary");
        } finally {
            setLoading(false);
        }
    };

    const fetchClosingBalance = async () => {
        if (!selectedStoreId || !startDate) return;

        try {
            const params = {
                storeId: selectedStoreId,
                date: startDate,
            };

            const response = await getData(
                "closingbalance/closing",
                params,
                sessionToken
            );

            // API returns: response.data.message.totals
            const totals = response?.data?.totals || {};
            setClosingTotals(totals);

        } catch (error) {
            toast.error(error.message || "Failed to fetch Closing Balance");
        }
    };



    useEffect(() => {
        fetchReportSummary();
    }, [selectedStoreId, startDate, endDate, filterType]);

    useEffect(() => {
        fetchClosingBalance();
    }, [selectedStoreId, startDate]);

    const onExportExcel = () => {
        if (!reportSummary) {
            toast.warning("No report data to export. Please fetch the report first.");
            return;
        }

        const sheets = {};

        const invoices = reportSummary?.invoices || {};
        const purchases = reportSummary?.purchases || {};
        const sales = reportSummary?.sales || {};
        const profit = reportSummary?.profit || 0;
        const loss = reportSummary?.loss || 0;
        const expense = reportSummary?.expenses || [];

        // Helper to format dates
        const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-GB") : "";

        // Function to create Summary sheet
        const createSummarySheet = () => {
            return [
                { Section: "INVOICES", Field: "Total Amount", Value: invoices.totalAmount || 0 },
                { Section: "INVOICES", Field: "Cash", Value: invoices.cash || 0 },
                { Section: "INVOICES", Field: "Card", Value: (invoices.card || 0) + (invoices.creditCard || 0) + (invoices.debitCard || 0) },
                { Section: "INVOICES", Field: "UPI", Value: (invoices.upi || 0) + (invoices.googlePay || 0) + (invoices.phonePe || 0) + (invoices.paytm || 0) + (invoices.online || 0) },
                { Section: "INVOICES", Field: "Wallet", Value: invoices.wallet || 0 },
                { Section: "INVOICES", Field: "Others", Value: (invoices.others || 0) + (invoices.cheque || 0) },

                { Section: "SALES", Field: "Total Amount", Value: sales.totalAmount || 0 },
                { Section: "SALES", Field: "No. of Bills", Value: sales.noOfBills || 0 },
                { Section: "SALES", Field: "Tax", Value: sales.tax || 0 },
                { Section: "SALES", Field: "Products Revenue", Value: sales.products || 0 },
                { Section: "SALES", Field: "Services Revenue", Value: sales.services || 0 },

                { Section: "PURCHASES", Field: "Total Amount", Value: purchases.totalAmount || 0 },
                { Section: "PURCHASES", Field: "Paid", Value: purchases.paid || 0 },
                { Section: "PURCHASES", Field: "Due", Value: purchases.due || 0 },

                { Section: "EXPENSES", Field: "Total", Value: expense.overallTotalAmount || 0 },
                { Section: "EXPENSES", Field: "Billable", Value: expense.totalBillableAmount || 0 },
                { Section: "EXPENSES", Field: "Non-Billable", Value: expense.totalNonBillableAmount || 0 },

                { Section: "PROFIT/LOSS", Field: "Profit", Value: profit },
                { Section: "PROFIT/LOSS", Field: "Loss", Value: loss },
            ];
        };

        // 1. SALES
        if (filterType === "All" || filterType === "Sales") {
            sheets["Sales Summary"] = [
                { Field: "Total Sales", Value: sales.totalAmount || 0 },
                { Field: "Total Bills", Value: sales.noOfBills || 0 },
                { Field: "Tax Amount", Value: sales.tax || 0 },
                { Field: "Product Revenue", Value: sales.products || 0 },
                { Field: "Service Revenue", Value: sales.services || 0 }
            ];
            sheets["Sales Details"] = (reportSummary.salesDetails || []).map(item => ({
                "Date": formatDate(item.invoiceDate),
                "Invoice No": item.invoiceNo,
                "Item Name": item.itemName,
                "Category": item.category,
                "Qty": item.qty,
                "Price": item.price,
                "Total": item.total
            }));
        }

        // 2. PURCHASES
        if (filterType === "All" || filterType === "Purchases") {
            sheets["Purchase Summary"] = [
                { Field: "Total Purchase", Value: purchases.totalPurchaseValue || 0 },
                { Field: "Paid Amount", Value: purchases.totalValueReceived || 0 },
                { Field: "Due Amount", Value: purchases.totalOutstanding || 0 },
            ];
            sheets["Purchase Details"] =
                (reportSummary.purchaseDetails || []).map(item => ({
                    Date: formatDate(item.purchaseDate),
                    "Invoice No": item.invoiceNo,
                    Supplier: item.vendorName,
                    Total: item.grandTotal,
                    Paid: item.paidAmount,
                    Due: item.dueAmount,
                    Status: item.status
                }));
        }

        // 3. PAYMENTS
        if (filterType === "All" || filterType === "Payments") {
            const invoices = reportSummary?.invoices || {};

            const cash = invoices.cash || 0;
            const card = (invoices.card || 0) + (invoices.creditCard || 0) + (invoices.debitCard || 0);
            const upi = (invoices.upi || 0) + (invoices.googlePay || 0) + (invoices.phonePe || 0) + (invoices.paytm || 0) + (invoices.online || 0);
            const wallet = invoices.wallet || 0;
            const others = (invoices.others || 0) + (invoices.cheque || 0);

            sheets["Payment Report"] = [
                { Method: "Cash", Amount: cash },
                { Method: "Card", Amount: card },
                { Method: "UPI", Amount: upi },
                { Method: "Wallet", Amount: wallet },
                { Method: "Others", Amount: others },
                { Method: "Total", Amount: cash + card + upi + wallet + others }
            ];

            // If paymentDetails exist
            if (reportSummary.paymentDetails) {
                const paymentRows = [];
                (reportSummary.paymentDetails || []).forEach(item => {
                    (item.paymentMode || []).forEach(pm => {
                        paymentRows.push({
                            "Date": formatDate(item.invoiceDate || item.date),
                            "Invoice No": item.invoiceNo,
                            "Customer": item.customerName,
                            "Phone": item.customerPhone,
                            "Method": pm.method,
                            "Amount": pm.amount,
                            "Grand Total": item.grandTotal
                        });
                    });
                });
                sheets["Payment Details"] = paymentRows;
            }
        }

        // 4. EXPENSES
        if (filterType === "All" || filterType === "Expense") {
            sheets["Expense Summary"] = [
                { Field: "Overall Total", Value: expense.overallTotalAmount || 0 },
                { Field: "Billable Amount", Value: expense.totalBillableAmount || 0 },
                { Field: "Non-Billable Amount", Value: expense.totalNonBillableAmount || 0 }
            ];
            sheets["Expense Details"] = (reportSummary.expenseDetails || []).map(item => ({
                "Date": formatDate(item.date),
                "Category": item.category,
                "Amount": item.amount,
                "Description": item.description
            }));
        }

        // 5. SUMMARY (Add if All)
        if (filterType === "All") {
            sheets["General Summary"] = createSummarySheet();
        }

        if (Object.keys(sheets).length === 0) {
            toast.warning("No data found for the selected filter.");
            return;
        }

        exportDataToExcel(
            sheets,
            null,
            `Report_${filterType}_${startDate}_to_${endDate}.xlsx`
        );

        toast.success(`${filterType} Report exported successfully!`);
    };


    useEffect(() => {
        const handleStoreChange = () => {
            const storeId = selectedStoreId;
            setSelectedStoreId(storeId);
        };
        window.addEventListener("storeChanged", handleStoreChange);
        return () => {
            window.removeEventListener("storeChanged", handleStoreChange);
        };
    }, []);



    const invoices = reportSummary?.invoices || {};
    const purchases = reportSummary?.purchases || {};
    const sales = reportSummary?.sales || {};
    const profit = reportSummary?.profit || 0;
    const loss = reportSummary?.loss || 0;
    const expenseDetails = reportSummary?.expenseDetails || [];
    const totalExpense = expenseDetails.reduce(
        (total, item) => total + (item.amount || 0),
        0
    );

    const cashExpense = expenseDetails
        .filter((item) => item.paymentMode === "CASH")
        .reduce((total, item) => total + (item.amount || 0), 0);

    const cardExpense = expenseDetails
        .filter((item) => item.paymentMode === "CARD")
        .reduce((total, item) => total + (item.amount || 0), 0);

    const upiExpense = expenseDetails
        .filter((item) =>
            ["UPI", "OTHERS"].includes(
                item.paymentMode
            )
        )
        .reduce((total, item) => total + (item.amount || 0), 0);

    const otherExpense = expenseDetails
        .filter((item) =>
            ![
                "CASH",
                "CARD",
                "UPI",
                "OTHERS"
            ].includes(item.paymentMode)
        )
        .reduce((total, item) => total + (item.amount || 0), 0);
    const closingBalance = closingTotals || {};
    const customers = reportSummary?.customers || { totalCustomers: 0 };



    // ✅ Dynamically calculate payment summary from reportSummary.paymentDetails
    const paymentMethodSummary = (reportSummary?.paymentDetails || []).reduce((acc, item) => {
        (item.paymentMode || []).forEach((m) => {
            const rawMethod = m.method?.toUpperCase() || "UNKNOWN";
            let methodName = "OTHERS";

            if (rawMethod === "CASH") {
                methodName = "CASH";
            } else if (["CARD", "CREDIT_CARD", "DEBIT_CARD"].includes(rawMethod)) {
                methodName = "CARD";
            } else if (["UPI", "GOOGLE PAY", "PHONEPE", "PAYTM", "ONLINE_PAYMENT"].includes(rawMethod)) {
                methodName = "UPI";
            } else {
                methodName = "OTHERS";
            }

            const amount = Number(m.amount) || 0;
            acc[methodName] = (acc[methodName] || 0) + amount;
        });

        return acc;
    }, {});

    return (
        <div className="summary-main-clean">
            <div className="report-summary-header">
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 800,
                        color: '#111827',
                        fontSize: '28px',
                        letterSpacing: '0.5px'
                    }}
                >
                    Reports Summary
                </Typography>
                <div className="report-filters-group" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <select
                        className="report-select-input"
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                    >
                        <option value="All">All Types</option>
                        <option value="Sales">Sales Only</option>
                        <option value="Purchases">Purchases Only</option>
                        <option value="Payments">Payments Only</option>
                        <option value="Expense">Expense Only</option>
                    </select>

                    <button
                        className="export-excel-btn"
                        onClick={onExportExcel}
                        style={{
                            justifyContent: 'center',
                            background: '#3C0008',
                            padding: '10px 24px',
                            borderRadius: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            fontSize: '14px',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Icon icon="mdi:file-excel-outline" width="20" /> Export Excel
                    </button>
                </div>
            </div>

            <div className="summary-cards-container">
                {/* Purchases Card */}
                {(filterType === "All" || filterType === "Purchases") && (
                    <div className="report-summary-card card-theme-purchases">
                        {/* SVG Illustration */}
                        <svg className="card-illustration" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M35 30V25C35 16.7157 41.7157 10 50 10C58.2843 10 65 16.7157 65 25V30" stroke="#7c3aed" strokeWidth="4" strokeLinecap="round" />
                            <rect x="25" y="30" width="50" height="55" rx="10" fill="#f5f3ff" stroke="#7c3aed" strokeWidth="4" />
                            <path d="M42 42V37C42 32.5817 45.5817 29 50 29C54.4183 29 58 32.5817 58 37V42" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round" />
                            <rect x="35" y="42" width="45" height="43" rx="8" fill="#ede9fe" stroke="#a78bfa" strokeWidth="3" />
                        </svg>
                        <div className="report-card-header">
                            <div className="card-header-icon-wrapper">
                                <Icon icon="solar:cart-large-linear" />
                            </div>
                            <span className="card-header-title">Purchases Summary</span>
                        </div>
                        <div className="report-card-body">
                            <div className="report-stats-list">
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-purple-light">
                                            <Icon icon="mdi:cash-multiple" />
                                        </div>
                                        <span className="report-stat-label">TOTAL AMOUNT</span>
                                    </div>
                                    <span className="report-stat-value"> {(purchases.totalPurchaseValue || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:check-circle-outline" />
                                        </div>
                                        <span className="report-stat-label">PAID AMOUNT</span>
                                    </div>
                                    <span className="report-stat-value"> {(purchases.totalValueReceived || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-red-light">
                                            <Icon icon="mdi:alert-circle-outline" />
                                        </div>
                                        <span className="report-stat-label">DUE BALANCE</span>
                                    </div>
                                    <span className="report-stat-value"> {(purchases.totalOutstanding || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sales Card */}
                {(filterType === "All" || filterType === "Sales") && (
                    <div className="report-summary-card card-theme-sales">
                        {/* SVG Illustration */}
                        <svg className="card-illustration" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="20" y="60" width="12" height="25" rx="3" fill="#e6f4ea" stroke="#10b981" strokeWidth="2" />
                            <rect x="40" y="45" width="12" height="40" rx="3" fill="#d1fae5" stroke="#10b981" strokeWidth="2" />
                            <rect x="60" y="30" width="12" height="55" rx="3" fill="#a7f3d0" stroke="#10b981" strokeWidth="2" />
                            <path d="M15 75 L35 55 L55 40 L85 15" stroke="#059669" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M75 15 H85 V25" stroke="#059669" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="report-card-header">
                            <div className="card-header-icon-wrapper">
                                <Icon icon="solar:graph-up-linear" />
                            </div>
                            <span className="card-header-title">Sales Summary</span>
                        </div>
                        <div className="report-card-body">
                            <div className="report-stats-list">
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:currency-inr" />
                                        </div>
                                        <span className="report-stat-label">GROSS SALES</span>
                                    </div>
                                    <span className="report-stat-value"> {(sales.totalAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:calculator-variant-outline" />
                                        </div>
                                        <span className="report-stat-label">TAX COLLECTED</span>
                                    </div>
                                    <span className="report-stat-value"> {(sales.tax || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:package-variant-closed" />
                                        </div>
                                        <span className="report-stat-label">PRODUCT REVENUE</span>
                                    </div>
                                    <span className="report-stat-value"> {(sales.products || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:face-agent" />
                                        </div>
                                        <span className="report-stat-label">SERVICE REVENUE</span>
                                    </div>
                                    <span className="report-stat-value"> {(sales.services || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Profit/Loss Card */}
                {(filterType === "All") && (
                    <div className="report-summary-card card-theme-profit-loss">
                        {/* SVG Illustration */}
                        <svg className="card-illustration" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 80 Q 30 75, 45 55 T 80 45 T 110 20" stroke="#f97316" strokeWidth="4" strokeLinecap="round" fill="none" />
                            <path d="M10 80 Q 30 75, 45 55 T 80 45 T 110 20 L 110 90 L 10 90 Z" fill="url(#orange-grad)" opacity="0.15" />
                            <defs>
                                <linearGradient id="orange-grad" x1="60" y1="20" x2="60" y2="90" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#f97316" />
                                    <stop offset="1" stopColor="#fff" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <div className="report-card-header">
                            <div className="card-header-icon-wrapper">
                                <Icon icon="solar:round-transfer-horizontal-linear" />
                            </div>
                            <span className="card-header-title">Profit / Loss</span>
                        </div>
                        <div className="report-card-body">
                            <div className="report-stats-list">
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:arrow-up-bold-circle-outline" />
                                        </div>
                                        <span className="report-stat-label">NET PROFIT</span>
                                    </div>
                                    <span className="report-stat-value profit-highlight"> {(profit || 0).toFixed(2)}</span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-red-light">
                                            <Icon icon="mdi:arrow-down-bold-circle-outline" />
                                        </div>
                                        <span className="report-stat-label">NET LOSS</span>
                                    </div>
                                    <span className="report-stat-value loss-highlight"> {(loss || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {(filterType === "All" || filterType === "Expense") && (<div className="report-summary-card card-theme-expense">

                    {/* Header */}
                    <div className="report-card-header">
                        <div className="card-header-icon-wrapper">
                            <Icon icon="solar:document-text-linear" />
                        </div>

                        <span className="card-header-title">
                            Expense Summary
                        </span>
                    </div>

                    {/* Body */}
                    <div className="report-card-body">
                        <div className="report-stats-list">

                            {/* Total Expense */}
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-blue-light">
                                        <Icon icon="mdi:cash-multiple" />
                                    </div>

                                    <span className="report-stat-label">
                                        TOTAL EXPENSE
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {totalExpense.toFixed(2)}
                                </span>
                            </div>

                            {/* Cash Expense */}
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-green-light">
                                        <Icon icon="mdi:cash" />
                                    </div>

                                    <span className="report-stat-label">
                                        CASH EXPENSE
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {cashExpense.toFixed(2)}
                                </span>
                            </div>

                            {/* Card Expense */}
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-red-light">
                                        <Icon icon="mdi:credit-card-outline" />
                                    </div>

                                    <span className="report-stat-label">
                                        CARD EXPENSE
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {cardExpense.toFixed(2)}
                                </span>
                            </div>

                            {/* Cash Expense */}
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-green-light">
                                        <Icon icon="mdi:cash" />
                                    </div>

                                    <span className="report-stat-label">
                                        UPI EXPENSE
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {upiExpense.toFixed(2)}
                                </span>
                            </div>

                            {/* Cash Expense */}
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-green-light">
                                        <Icon icon="mdi:cash" />
                                    </div>

                                    <span className="report-stat-label">
                                        OTHER EXPENSE
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {otherExpense.toFixed(2)}
                                </span>
                            </div>

                        </div>
                    </div>
                </div>
                )}


                {/* Closing Balance Card */}
                {(filterType === "All" || filterType === "Payments") && (
                    <div className="report-summary-card card-theme-closing">
                        {/* SVG Illustration */}
                        <svg className="card-illustration" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="20" y="24" width="60" height="52" rx="10" fill="#fffbeb" stroke="#f59e0b" strokeWidth="4" />
                            <circle cx="50" cy="50" r="14" stroke="#f59e0b" strokeWidth="3" fill="#fef3c7" />
                            <line x1="50" y1="36" x2="50" y2="40" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                            <line x1="50" y1="60" x2="50" y2="64" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                            <line x1="36" y1="50" x2="40" y2="50" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                            <line x1="60" y1="50" x2="64" y2="50" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                        <div className="report-card-header">
                            <div className="card-header-icon-wrapper">
                                <Icon icon="solar:wallet-2-linear" />
                            </div>
                            <span className="card-header-title">Closing Cash Balance</span>
                        </div>
                        <div className="report-card-body">
                            <div className="report-stats-list">
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-blue-light">
                                            <Icon icon="mdi:safe" />
                                        </div>
                                        <span className="report-stat-label">
                                            OPENING CASH
                                        </span>
                                    </div>

                                    <span className="report-stat-value">
                                        {(closingBalance.openingCash || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-red-light">
                                            <Icon icon="mdi:bank-transfer-out" />
                                        </div>
                                        <span className="report-stat-label">
                                            BANK TRANSFER
                                        </span>
                                    </div>

                                    <span className="report-stat-value">
                                        {(closingBalance.bankTransfer || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-red-light">
                                            <Icon icon="mdi:account-cash-outline" />
                                        </div>
                                        <span className="report-stat-label">
                                            SELF WITHDRAWAL
                                        </span>
                                    </div>

                                    <span className="report-stat-value">
                                        {(closingBalance.selfWithdrawal || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-orange-light">
                                            <Icon icon="mdi:wallet" />
                                        </div>

                                        <span className="report-stat-label">
                                            CLOSING BALANCE
                                        </span>
                                    </div>

                                    <span className="report-stat-value">
                                        {(closingBalance.closingCash || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            <div className="report-stat-row">
                                <div className="report-stat-left">
                                    <div className="stat-icon-wrapper-small icon-red-light">
                                        <Icon icon="mdi:clock-alert-outline" />
                                    </div>

                                    <span className="report-stat-label">
                                        SELF / PENDING BILLS
                                    </span>
                                </div>

                                <span className="report-stat-value">
                                    {(closingBalance.pendingSelfSales || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payments Breakdown Card */}
                {(filterType === "All" || filterType === "Payments") && (
                    <div className="report-summary-card card-theme-payments">
                        {/* SVG Illustration */}
                        <svg className="card-illustration" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="50" cy="50" r="35" stroke="#a78bfa" strokeWidth="3" fill="#f5f3ff" />
                            <path d="M50 50 L50 15 A35 35 0 0 1 85 50 Z" fill="#8b5cf6" opacity="0.6" />
                            <path d="M50 50 L85 50 A35 35 0 0 1 50 85 Z" fill="#c084fc" opacity="0.4" />
                            <circle cx="50" cy="50" r="12" fill="#fff" stroke="#a78bfa" strokeWidth="2" />
                        </svg>
                        <div className="report-card-header">
                            <div className="card-header-icon-wrapper">
                                <Icon icon="solar:card-2-linear" />
                            </div>
                            <span className="card-header-title">Payments Breakdown</span>
                        </div>
                        <div className="report-card-body">
                            <div className="report-stats-list">
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-green-light">
                                            <Icon icon="mdi:cash" />
                                        </div>
                                        <span className="report-stat-label">CASH</span>
                                    </div>
                                    <span className="report-stat-value">
                                        {(invoices.cash || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-purple-light">
                                            <Icon icon="mdi:bank-transfer" />
                                        </div>
                                        <span className="report-stat-label">UPI</span>
                                    </div>
                                    <span className="report-stat-value">
                                        {(invoices.upi || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-blue-light">
                                            <Icon icon="mdi:credit-card-outline" />
                                        </div>
                                        <span className="report-stat-label">CARD</span>
                                    </div>
                                    <span className="report-stat-value">
                                        {(invoices.card || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-teal-light" style={{ background: '#e6fffa', color: '#0d9488' }}>
                                            <Icon icon="mdi:wallet-outline" />
                                        </div>
                                        <span className="report-stat-label">WALLET</span>
                                    </div>
                                    <span className="report-stat-value">
                                        {(invoices.wallet || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="report-stat-row">
                                    <div className="report-stat-left">
                                        <div className="stat-icon-wrapper-small icon-orange-light">
                                            <Icon icon="mdi:dots-horizontal" />
                                        </div>
                                        <span className="report-stat-label">OTHERS</span>
                                    </div>
                                    <span className="report-stat-value">
                                        {(invoices.others || 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default ReportPage;
